import { List, Map } from "immutable";
import { useCallback, useEffect, useRef, useState } from "react";
import { connect, ConnectedProps } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { RootReduxState } from "../../../../../utils/types";
import { startAuthProcessAction } from "../../redux/auth/actions";
import { addIntegrationAction, deleteIntegrationAction } from "../../redux/shopData/actions";
import { IntegrationType } from "../../types";
import { OAuthWindow } from "./OAuthWindow";

type OAuthSetupComponentOwnProps = {
  integrationType: IntegrationType;
  onFinish: () => void;
};

const mapStateToProps = (state: RootReduxState) => {
  const selectedShop = state.getIn([
    "customerBackendDashboard",
    "userData",
    "selectedShop"
  ]) as string;

  return {
    selectedShop,
    adding: state.getIn([
      "customerBackendDashboard",
      "shops",
      selectedShop,
      "integrations",
      "UIStates",
      "adding"
    ]) as boolean | undefined,
    integrations: state.getIn([
      "customerBackendDashboard",
      "shops",
      selectedShop,
      "integrations",
      "data"
    ]) as Map<unknown, unknown> | undefined
  };
};
const matchDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      addIntegrationAction,
      startAuthProcessAction,
      deleteIntegrationAction
    },
    dispatch
  );
const connector = connect(mapStateToProps, matchDispatchToProps);

type OAuthSetupComponentProps = OAuthSetupComponentOwnProps & ConnectedProps<typeof connector>;

function OAuthSetupComponent(props: OAuthSetupComponentProps) {
  const {
    selectedShop,
    startAuthProcessAction,
    deleteIntegrationAction,
    addIntegrationAction,
    adding = false,
    integrations = Map(),
    integrationType,
    onFinish
  } = props;

  const [addedId, setAddedId] = useState<number | undefined>(undefined);
  const authStarted = useRef(false);

  const calculateNewId = (type: string) => {
    if (integrations.size === 0) {
      return 0;
    }
    if (!integrations.get(type)) {
      return 0;
    }
    return (
      (integrations.get(type) as Map<unknown, unknown>[]).reduce(
        (p, c) => ((c.get("id") as number) > p ? (c.get("id") as number) : p),
        0
      ) + 1
    );
  };

  const integrationLineAdded = () =>
    (integrations.getIn([integrationType]) as List<Map<unknown, unknown>> | undefined)?.find(
      (settingLine) => settingLine?.get("id") === addedId
    );

  const authReady = addedId !== undefined && Boolean(integrationLineAdded());

  useEffect(() => {
    const newId = calculateNewId(integrationType);
    setAddedId(newId);
    addIntegrationAction(selectedShop, integrationType, { id: newId });
  }, []);

  useEffect(() => {
    if (adding || !authReady || authStarted.current || addedId === undefined) {
      return;
    }

    authStarted.current = true;
    startAuthProcessAction(integrationType, selectedShop, addedId);
  }, [addedId, adding, authReady, integrationType, selectedShop, startAuthProcessAction]);

  const handleAuthWindowClose = useCallback(() => {
    if (!addedId) {
      return;
    }

    deleteIntegrationAction(selectedShop, integrationType, addedId);
    setAddedId(undefined);
    onFinish();
  }, [addedId, selectedShop, integrationType, deleteIntegrationAction, onFinish]);

  const handleAuthWindowSuccess = useCallback(() => {
    setAddedId(undefined);
    onFinish();
  }, [onFinish]);

  return (
    authReady && (
      <OAuthWindow
        integrationType={integrationType}
        selectedShop={selectedShop}
        id={addedId}
        onSuccess={handleAuthWindowSuccess}
        onClose={handleAuthWindowClose}
      />
    )
  );
}

export const OAuthSetup = connector(OAuthSetupComponent);
